<template>
  <v-layout row wrap class="no-toppad" no-gutters text-xs-right>
    <v-container>
      <v-row noGutters>
        <v-col cols="12" md="12" sm="12">
          <div class="sections-container">
            <div class="modern-form">
              <div class="form-container">
                <div class="section-header">
                  <h2 class="section-title">Account Access Permissions</h2>
                </div>
                <v-progress-circular
                  v-if="loading"
                  indeterminate
                  color="primary"
                  class="loading-spinner" />
                <div v-if="!loading" class="consent-content">
                  <div class="access-section">
                    <h4 class="access-title">You are able to view the following patient's accounts</h4>
                    <div class="patient-list">
                      <div v-if="consentData.viewablePatients?.length" class="patient-items">
                        <div v-for="patient of consentData.viewablePatients" :key="`view-${patient.id}`" class="patient-item">
                          <div class="patient-info">
                            <v-avatar size="36" class="patient-avatar">
                              {{ getInitials(patient.firstName, patient.lastName) }}
                            </v-avatar>
                            <span class="patient-name">{{ patient.firstName }} {{ patient.lastName }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-else class="no-patients">
                        You cannot view any other patients' accounts
                      </div>
                    </div>
                  </div>

                  <div class="access-section">
                    <h4 class="access-title">Your account is viewable by the following patients</h4>
                    <div class="patient-list">
                      <div v-if="consentData.patientsAbleToView?.length" class="patient-items">
                        <div v-for="patient of consentData.patientsAbleToView" :key="`able-${patient.id}`" class="patient-item with-action">
                          <div class="patient-info">
                            <v-avatar size="36" class="patient-avatar">
                              {{ getInitials(patient.firstName, patient.lastName) }}
                            </v-avatar>
                            <span class="patient-name">{{ patient.firstName }} {{ patient.lastName }}</span>
                          </div>
                          <v-btn class="btn-warning" outlined @click="openRevokeAccessDialog(patient)">Revoke</v-btn>
                        </div>
                      </div>
                      <div v-else class="no-patients">
                        No patients can view your account
                      </div>
                    </div>
                  </div>

                  <div v-if="eligiblePatients.length" class="access-section">
                    <h4 class="access-title">You may provide access to view your account to the following patients</h4>
                    <div class="patient-list">
                      <div class="patient-items">
                        <div v-for="patient of eligiblePatients" :key="`elig-${patient.id}`" class="patient-item with-action">
                          <div class="patient-info">
                            <v-avatar size="36" class="patient-avatar">
                              {{ getInitials(patient.firstName, patient.lastName) }}
                            </v-avatar>
                            <span class="patient-name">{{ patient.firstName }} {{ patient.lastName }}</span>
                          </div>
                          <v-btn class="btn-primary" @click="openConfirmAccessDialog(patient)">Grant Access</v-btn>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="showConfirmAccessDialog" max-width="500px" persistent>
      <v-card class="modern-modal">
        <v-card-title class="modal-title">Confirm Granting Access</v-card-title>
        <v-progress-circular
            v-if="saving"
            indeterminate
            color="primary"
            class="loading-spinner" />
        <v-card-text class="modal-content" v-else>
          <p class="modal-question">Grant {{ dialogPatient.firstName }} {{ dialogPatient.lastName }} access to view your account?</p>
          <div class="info-section">
            <p class="info-header">They will be able to view:</p>
            <ul class="info-list">
              <li>Demographic information, including address and date of birth</li>
              <li>Billing information, including amounts billed & owed as well as payment history</li>
              <li>Upcoming and past appointments</li>
              <li>Patient forms</li>
            </ul>
            <p class="info-footer">You may revoke this access at any time from this page</p>
          </div>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn class="btn-primary" @click="saveAccessToPatient()">Grant Access</v-btn>
          <v-btn class="btn-secondary" @click="showConfirmAccessDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showRevokeAccessDialog" max-width="500px" persistent>
      <v-card class="modern-modal">
        <v-card-title class="modal-title">Confirm Revoking Access</v-card-title>
        <v-progress-circular
            v-if="saving"
            indeterminate
            color="primary"
            class="loading-spinner" />
        <v-card-text class="modal-content" v-else>
          <p class="modal-question">Are you sure you wish to remove {{ dialogPatient.firstName }} {{ dialogPatient.lastName }}'s ability to view your account?</p>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn class="btn-warning" @click="revokeAccessForPatient()">Revoke Access</v-btn>
          <v-btn class="btn-secondary" @click="showRevokeAccessDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>
<script>
import DataService from '../../../_services/DataService';

export default {
  name: 'PrivacyConsent',
  data() {
    return {
      loading: false,
      saving: false,
      consentData: {
        viewablePatients: [],
        patientsAbleToView: [],
      },
      eligiblePatients: [],
      showConfirmAccessDialog: false,
      showRevokeAccessDialog: false,
      dialogPatient: {},
    };
  },
  methods: {
    getInitials(firstName, lastName) {
      const first = firstName && firstName.length > 0 ? firstName.charAt(0) : '';
      const last = lastName && lastName.length > 0 ? lastName.charAt(0) : '';
      return first + last;
    },
    openConfirmAccessDialog(patient) {
      this.showRevokeAccessDialog = false;
      this.showConfirmAccessDialog = true;
      this.dialogPatient = patient;
    },
    openRevokeAccessDialog(patient) {
      this.showRevokeAccessDialog = true;
      this.showConfirmAccessDialog = false;
      this.dialogPatient = patient;
    },
    async saveAccessToPatient() {
      try {
        this.saving = true;
        const response = await DataService.grantPatientViewingAccess(this.dialogPatient.id);
        if (response.status == 201) {
          this.$root.$emit('raise_alert', { color: 'success', text: 'Access granted successfully' });
          this.showConfirmAccessDialog = false;
          this.loading = true;
          await this.loadConsentData();
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: response.data || 'Something went wrong',
          });
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: 'Something went wrong',
        });
      } finally {
        this.saving = false;
      }
    },
    async revokeAccessForPatient() {
      try {
        this.saving = true;
        const response = await DataService.revokePatientConsent(this.dialogPatient.id);
        if (response.status == 200) {
          this.$root.$emit('raise_alert', { color: 'success', text: 'Access revoked successfully' });
          this.showRevokeAccessDialog = false;
          this.loading = true;
          await this.loadConsentData();
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: response.data || 'Something went wrong',
          });
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: 'Something went wrong',
        });
      } finally {
        this.saving = false;
      }
    },
    async loadConsentData() {
      this.loading = true;

      try {
        const [consentResponse, eligiblePatientResponse] = await Promise.all([
          DataService.getPatientConsent(),
          DataService.getEligiblePatientsForConsent(),
        ]);

        if (consentResponse.status === 200 && eligiblePatientResponse.status === 200) {
          this.consentData = consentResponse.data;
          this.eligiblePatients = eligiblePatientResponse.data;
        } else {
          this.$root.$emit('raise_alert', { color: 'info', text: 'Unable to load consent data at this time' });
        }
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to load consent data' });
      }

      this.loading = false;
    },
  },
  async mounted() {
    await this.loadConsentData();
  },
};
</script>

<style lang="scss">
.sections-container {
  padding-bottom: 32px;
}

.consent-content {
  .access-section {
    margin-bottom: 32px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .access-title {
      font-size: 16px;
      font-weight: 600;
      color: #374151;
      margin: 0 0 16px 0;
    }
  }
  
  .patient-list {
    .patient-items {
      .patient-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        margin-bottom: 8px;
        min-height: 56px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        &:not(.with-action) {
          justify-content: flex-start;
        }
        
        .patient-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .patient-avatar {
          background-color: #133b54 !important;
          color: white;
        }
        
        .patient-name {
          font-size: 14px;
          color: #1f2937;
          font-weight: 500;
        }
      }
    }
    
    .no-patients {
      padding: 16px;
      text-align: center;
      color: #6b7280;
      font-style: italic;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
    }
  }
}
</style>
